import locale from "@bonliva-crm/locale";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { useMemo } from "react";
import { ExternalActionStepProps } from "../utils/action-step-props";
import { BulkActionHookReturn } from "../utils/bulk-action-data";
import { SelectionStepRowType } from "../utils/selection-step-row-type";
import { useBulkActions } from "./use-bulk-actions";

type Props<T extends SelectionStepRowType> = {
  valueParser: ExternalActionStepProps<T>["valueParsers"]["ConsultantDelete"];
  selectedRows: T[];
  totalRows: number;
  entitySingular: string;
  entityPlural: string;
};

export const useConsultantDeleteAction = <T extends SelectionStepRowType>(
  props: Props<T>
): BulkActionHookReturn => {
  const classes = useStyles();
  const { valueParser, selectedRows, totalRows, entitySingular, entityPlural } = props;

  const parsedValues = useMemo(
    () => valueParser(selectedRows),
    [valueParser, selectedRows]
  );

  const { deleteConsultants } = useBulkActions({});

  const rowsWithoutResponsiblePersons = parsedValues.nodeIds.length;

  const confirmationContent = (
    <Box className={classes.confirmationContent}>
      {totalRows !== rowsWithoutResponsiblePersons && (
        <Typography variant="body1">
          {locale.bulkActionDeleteNotValid(
            totalRows,
            rowsWithoutResponsiblePersons,
            entitySingular,
            entityPlural,
          )}
        </Typography>
      )}
      <Typography variant="body1">
        {locale.bulkActionConfirmation(
          `${locale.Delete}`,
          rowsWithoutResponsiblePersons,
          entitySingular,
          entityPlural
        )}
      </Typography>
      <Typography variant="body1">{locale.youWillBeNotifiedViaEmail}</Typography>
    </Box>
  );

  const onSubmit = () =>
    deleteConsultants({
      consultantIds: parsedValues.nodeIds
    });

  return {
    actionContent: null,
    confirmationContent,
    onSubmit,
    reset: () => {},
    nextStepDisabled: false
  };
};

const useStyles = makeStyles((theme) => ({
  confirmationContent: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem"
  }
}));
